import { type useModalState } from "@clipboard-health/ui-components";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { UpdateChartingCredentialsBottomSheet } from "@src/appV2/redesign/Charting/UpdateChartingCredentialsBottomSheet";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useUpdateWorkerCredentials } from "@src/appV2/Shifts/Shift/useUpdateWorkerCredentials";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { invalidateChatDetectedChartingCredentials } from "./api/useGetChatDetectedChartingCredentials";

export interface UpdateChartingCredentialsContainerProps {
  modalState: ReturnType<typeof useModalState>;
  shiftId?: string;
  workplaceId: string;
  workerId: string;
  chartingCredentials?: ChartingCredential;
  defaultCredentials?: ChartingCredential;
  showPassword?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
  source?: "chat-detected-and-saved" | "worker";
}

export function UpdateChartingCredentialsContainer({
  modalState,
  shiftId,
  workplaceId,
  workerId,
  chartingCredentials,
  defaultCredentials,
  showPassword = false,
  onSuccess,
  onClose,
  source,
}: UpdateChartingCredentialsContainerProps) {
  const queryClient = useQueryClient();
  // Do not automatically invalidate related queries on success. Since we need to close the modal first
  // to prevent reteching credentials and showing an updated UI, confusing the user.
  const { mutateAsync: updateCredentials } = useUpdateWorkerCredentials(false);
  const { showErrorToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (username: string, password: string) => {
    setIsSaving(true);
    try {
      // Only make API call if credentials have actually changed
      if (
        username !== chartingCredentials?.username ||
        password !== chartingCredentials?.password
      ) {
        await updateCredentials({
          workerId,
          workplaceId,
          chartingCredentials: {
            software: chartingCredentials?.software,
            username,
            password,
            source: source ?? "worker",
          },
        });

        await invalidateChatDetectedChartingCredentials(queryClient, {
          workerId,
          workplaceId,
          status: "pending",
        });
      }

      modalState.closeModal();
      await queryClient.invalidateQueries([
        `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
        { filter: { workerId, workplaceId } },
      ]);
      await queryClient.invalidateQueries([
        `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
      ]);

      onSuccess?.();
    } catch (error) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_CHARTING_CREDENTIALS_UPDATE_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        workerId,
        workplaceId,
        shiftId,
      });

      const errorMessage = "Failed to save credentials. Please try again.";
      showErrorToast(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    modalState.closeModal();
    onClose?.();
  };

  return (
    <UpdateChartingCredentialsBottomSheet
      modalState={modalState}
      shiftId={shiftId}
      workplaceId={workplaceId}
      workerId={workerId}
      showPassword={showPassword}
      chartingCredentials={chartingCredentials}
      defaultCredentials={defaultCredentials}
      isSaving={isSaving}
      onSave={handleSave}
      onClose={handleClose}
    />
  );
}
